import { Badge, Box, Grid, Heading, Image, Skeleton, Text, VStack } from '@chakra-ui/react';
import { format } from 'date-fns';
import { useEffect, useState } from 'react';
import { userService } from '../../../data/services/userService';

export default function Vouchers() {
    const [vouchers, setVouchers] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchVouchers = async () => {
            try {
                setIsLoading(true);
                const data = await userService.getVouchers();
                setVouchers(data);
            } catch (err) {
                setError(err.message || 'Failed to fetch vouchers');
            } finally {
                setIsLoading(false);
            }
        };

        fetchVouchers();
    }, []);

    const formatDate = (dateString) => {
        try {
            return format(new Date(dateString), 'dd MMM yyyy');
        } catch {
            return dateString;
        }
    };

    return (
        <VStack spacing={{ base: 4, md: 6 }} align='stretch' w='full'>
            <Box mt={{ base: 5, md: 0 }} mb={3}>
                <Heading size={{ base: 'md', md: 'lg' }} color='gray.800' mb={{ base: 0, md: 2 }} textAlign={{ base: 'center', md: 'left' }}>
                    Vouchers
                </Heading>
                <Text color='gray.600' fontSize={{ base: 'sm', md: 'md' }} textAlign={{ base: 'center', md: 'left' }}>
                    Your available vouchers and discounts
                </Text>
            </Box>

            {isLoading ? (
                <Grid templateColumns={{ base: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }} gap={{ base: 4, md: 6 }}>
                    {[1, 2, 3].map((i) => (
                        <Skeleton key={i} height='250px' borderRadius='2xl' />
                    ))}
                </Grid>
            ) : vouchers.length === 0 ? (
                <Box bg='white' borderRadius='2xl' p={{ base: 8, md: 12 }} border='1px' borderColor='gray.100' textAlign='center'>
                    <Text fontSize='6xl' mb={4}>
                        🎟️
                    </Text>
                    <Text color='gray.500' fontSize={{ base: 'md', md: 'lg' }}>
                        No vouchers available
                    </Text>
                </Box>
            ) : (
                <Grid templateColumns={{ base: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }} gap={{ base: 4, md: 6 }}>
                    {vouchers.map((voucher) => (
                        <Box
                            key={voucher.id}
                            bg='white'
                            borderRadius='2xl'
                            overflow='hidden'
                            border='1px'
                            borderColor='gray.100'
                            _hover={{
                                transform: 'translateY(-4px)',
                            }}
                            transition='all 0.3s'>
                            {voucher.imageUrl && <Image src={voucher.imageUrl} alt={voucher.name} h='150px' w='full' objectFit='cover' />}
                            <Box p={{ base: 4, md: 5 }}>
                                <Badge colorScheme={voucher.isActive() ? 'green' : 'gray'} mb={2} borderRadius='full' px={3} fontSize='xs'>
                                    {voucher.status}
                                </Badge>
                                <Heading size='sm' mb={2} color='gray.800'>
                                    {voucher.name}
                                </Heading>
                                <Text fontSize='sm' color='gray.600' mb={3} noOfLines={2}>
                                    {voucher.description}
                                </Text>
                                <Text fontSize='xs' color='gray.500'>
                                    Valid until: {formatDate(voucher.expiryDate)}
                                </Text>
                                <Text fontSize={{ base: 'md', md: 'lg' }} fontWeight='bold' color='purple.600' mt={2}>
                                    {voucher.discount}
                                </Text>
                            </Box>
                        </Box>
                    ))}
                </Grid>
            )}
        </VStack>
    );
}
